import { getIdToken } from './firebase';

const API_ROOT        = (process.env.REACT_APP_API_BASE_URL || 'https://ettht19es1.execute-api.eu-north-1.amazonaws.com/prod').replace(/\/$/, '');
const API_BASE_URL    = `${API_ROOT}/api`;

/** Public root of the tracking API, for the documentation examples shown to developers. */
export const API_PUBLIC_ROOT = API_ROOT;
const STRIPE_BASE_URL = (process.env.REACT_APP_STRIPE_BASE_URL || API_BASE_URL).replace(/\/$/, '');
const IOTEC_BASE_URL  = (process.env.REACT_APP_IOTEC_BASE_URL  || STRIPE_BASE_URL).replace(/\/$/, '');

const NETWORK_ERROR_MESSAGE = 'Please check your network and try again.';
const SERVER_ERROR_MESSAGE = 'Please try again later.';
const SIGNED_OUT_MESSAGE = 'Your session has expired. Please sign in again.';
const UNVERIFIED_MESSAGE = 'Verify your email address to continue. Check your inbox for the verification link.';

/**
 * Attaches the signed-in user's Firebase ID token. The API requires it on every
 * consumer endpoint; a browser must never carry a developer API key instead.
 */
const authHeaders = async () => {
  const token = await getIdToken();
  if (!token) {
    const error = new Error(SIGNED_OUT_MESSAGE);
    error.status = 401;
    throw error;
  }
  return { Authorization: `Bearer ${token}` };
};

/**
 * Track a shipment using the provided tracking ID.
 *
 * @param {string} trackingId - The tracking ID of the shipment
 * @returns {Promise<Object>} - The tracking data
 * @throws {Error} - If the API request fails
 */
const apiFetch = async (baseUrl, path, options = {}) => {
  let response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
  } catch (error) {
    const networkError = new Error(NETWORK_ERROR_MESSAGE);
    networkError.isNetworkError = true;
    networkError.cause = error;
    throw networkError;
  }
  // Guard against non-JSON responses (HTML error pages, gateway errors, etc.)
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json')
    ? await response.json()
    : await response.text().then(() => ({}));
  if (!response.ok) {
    const requestError = new Error(data.message || `Request failed (${response.status}): ${path}`);
    requestError.status = response.status;
    requestError.code = data.error;
    throw requestError;
  }
  // Unwrap backend envelope { success, data: {...} } if present
  return data.data !== undefined ? data.data : data;
};

export const trackShipment = async (trackingId) => {
  try {
    return await apiFetch(API_BASE_URL, '/track', {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ trackingId }),
    });
  } catch (error) {
    console.error('API Error:', error);
    if (error?.isNetworkError) throw new Error(NETWORK_ERROR_MESSAGE);
    // Auth failures are actionable by the user, so say what to do about them
    // rather than collapsing everything into "try again later".
    if (error?.code === 'EMAIL_NOT_VERIFIED') throw new Error(UNVERIFIED_MESSAGE);
    if (error?.status === 401) throw new Error(SIGNED_OUT_MESSAGE);
    throw new Error(SERVER_ERROR_MESSAGE);
  }
};

// ── Developer API keys ───────────────────────────────────────────────────────

/** Lists the signed-in user's API keys. Key material is never returned here. */
export const listApiKeys = async () =>
  apiFetch(API_BASE_URL, '/developer/keys', { headers: await authHeaders() });

/**
 * Creates the account's single API key.
 * The `key` field on the result is the only time the secret is available.
 */
export const createApiKey = async (label) =>
  apiFetch(API_BASE_URL, '/developer/keys', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ label }),
  });

/** Revokes a key permanently, freeing the account's key slot. */
export const revokeApiKey = async (keyId) =>
  apiFetch(API_BASE_URL, `/developer/keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });

// ── Credit & Payment APIs (uses REACT_APP_STRIPE_BASE_URL) ───────────────────

export const createStripeCustomer = (userId, email, name) =>
  apiFetch(STRIPE_BASE_URL, '/payments/customers', {
    method: 'POST',
    body: JSON.stringify({ email, name }),
  });

export const initializeWallet = (userId) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/initialize`, {
    method: 'POST',
    body: JSON.stringify({ userId, creditType: 'sea' }),
  });

export const purchaseCredits = (userId, creditCount, paymentMethodId, stripeCustomerId) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/purchase`, {
    method: 'POST',
    body: JSON.stringify({ creditCount, paymentMethodId, creditType: 'sea', stripeCustomerId }),
  });

export const getWallet = (userId) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/wallet`);

export const checkCredits = (userId) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/check?creditType=sea`);

export const consumeCredits = (userId, trackingNumber) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/consume`, {
    method: 'POST',
    body: JSON.stringify({ trackingNumber, creditType: 'sea' }),
  });

export const confirmPurchase = (userId, paymentIntentId) =>
  apiFetch(STRIPE_BASE_URL, `/credits/${userId}/confirm-purchase`, {
    method: 'POST',
    body: JSON.stringify({ paymentIntentId }),
  });

export const getStripeCustomer = (customerId) =>
  apiFetch(STRIPE_BASE_URL, `/payments/customers/${customerId}`);

export const listPaymentMethods = (userId) =>
  apiFetch(STRIPE_BASE_URL, `/payments/customers/${userId}/payment-methods`);

export const attachPaymentMethod = (userId, paymentMethodId) =>
  apiFetch(STRIPE_BASE_URL, `/payments/customers/${userId}/payment-methods`, {
    method: 'POST',
    body: JSON.stringify({ paymentMethodId }),
  });

// ── Iotec Mobile Money APIs ───────────────────────────────────────────────────

export const initiateMobileMoneyPayment = (payer, amount, orderId) =>
  apiFetch(IOTEC_BASE_URL, '/Iotech/collectPayment', {
    method: 'POST',
    body: JSON.stringify({ payer, amount, orderId }),
  });


